import { Box, Theme, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { ButtonDefault } from '../../shared/components/ButtonDefault';

import { useAuth } from '../auth/auth-hook';
import { getPostUrlWithoutQuery } from '../auth/auth-utils';
import {
  AddUserBroadcastDataInput,
  PaymentMethod,
  Plan,
  StripeCoupon,
  StudentCredentialInput,
  Subscription,
  SubscriptionBillingInput,
  useAddUserPreferencesDataMutation,
  useCheckoutPlanSubscriptionMutation,
  useCreateStudentCredentialMutation,
  useUpdatePlanSubscriptionMutation,
  useUpdateSubscriptionMutation,
} from '../../shared/types/generated';
import { openDialogAlert } from '../alert/alert-actions';
import { useEffect, useState } from 'react';
import { clientUpload } from '../../shared/apollo';
import { Interests } from '../payment/payment-types';
import { translateInterests } from '../payment/payment-utils';
import { triggerEvent } from '../remote-education/remote-education-utils';
import { getRedirectsOnPurchaseSub } from '../../shared/utils';

const useStyles = makeStyles<Theme>((theme: Theme) =>
  createStyles({
    icon: {
      color: '#999999',
      fontSize: '20px!important',
      paddingRight: '5px',
    },
    input: {
      background: 'white',
      fontSize: '18px',
      height: '48px',
    },
    button: {
      borderRadius: '5px!important',
      backgroundColor: '#FF3C41 !important',
      border: 'none!important',
      color: 'white!important',
      fontFamily: 'oswald !important',
      fontWeight: '400 !important',
      textTransform: 'uppercase',
      fontSize: '20px !important',
      lineHeight: '20px !important',
      height: '55px !important',
      '&:disabled': {
        backgroundColor: '#FFCCCC!important',
      },
      [theme.breakpoints.down('md')]: {
        fontSize: '16px !important',
        lineHeight: '16px !important',
        height: '45px !important',
      },
    },
  }),
);

interface StripeButtonProps {
  subscriptionBilling: SubscriptionBillingInput | null;
  studentCredential: StudentCredentialInput;
  coupon?: StripeCoupon | null;
  activeSubscription?: Subscription;
  plan?: Plan;
  interests?: Interests[];
  broadcastData?: AddUserBroadcastDataInput;
  studentTerms?: boolean;
}

export const StripeButtonWithoutStore: React.FC<StripeButtonProps> = ({
  subscriptionBilling,
  studentTerms,
  studentCredential,
  coupon,
  activeSubscription,
  plan,
  interests,
  broadcastData,
}) => {
  const [loading, setLoading] = useState(false);
  const classes = useStyles();
  const { user } = useAuth();
  const [billingDisabled, setBillingDisabled] = useState(true);
  //const { plan } = useStore(changePlanStore);

  const [updateUserPreferences] = useAddUserPreferencesDataMutation({
    onError: () => {
      openDialogAlert('Hubo un error al procesar las preferencias del usuario');
    },
  });

  const [updateUserSubscription] = useUpdateSubscriptionMutation({
    refetchQueries: ['CurrentUser'],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      if (data?.updateSubscription) {
        setLoading(false);
        openDialogAlert(
          'Suscripción actualizada',
          () =>
            (window.location.href = getRedirectsOnPurchaseSub(plan as Plan)),
        );
      }
    },
    onError: (error) => {
      setLoading(false);
      console.error(error);
      openDialogAlert(error.message);
    },
  });

  const [callCheckout] = useCheckoutPlanSubscriptionMutation({
    onCompleted: (data) => {
      if (!data.checkoutPlanSubscription?.redirect_url) return;
      window.location.href = data.checkoutPlanSubscription
        ?.redirect_url as string;
    },
    onError: (error) => {
      setLoading(false);
      openDialogAlert(
        'Hubo un error al solicitar el pago, intente de nuevo mas tarde',
      );
      console.error(error);
    },
  });

  // const [callCheckoot] = useCallAction(checkoutPlanSubscription, {
  //   onError: (error) => {
  //     setLoading(false);
  //     console.error(error);
  //   },
  // });

  const [callUpdate, { loading: loadingUpdate }] =
    useUpdatePlanSubscriptionMutation({
      onCompleted() {
        updateUserSubscription({
          variables: {
            data: {
              id: activeSubscription?.id,
              payment_method_type: PaymentMethod.StripeCard,
              plan_id: plan?.id,
              subscription_id: activeSubscription?.transaction_id as string,
              subscriptionBilling,
            },
          },
        });
      },
      onError: (error) => {
        setLoading(false);
        console.error(error);
        openDialogAlert(
          'Error al actualizar la suscripción. No se pudo agendar el pago, intente nuevamente',
        );
      },
    });

  // const [callUpdate, loadingUpdate] = useCallAction(updatePlanSubscription, {
  //     onCompleted() {
  //         updateUserSubscription({
  //             variables: {
  //                 data: {
  //                     id: activeSubscription?.id,
  //                     payment_method_type: PaymentMethod.StripeCard,
  //                     plan_id: plan?.id,
  //                     subscription_id: activeSubscription?.transaction_id as string,
  //                     subscriptionBilling,
  //                 },
  //             },
  //         });
  //     },
  //     onError: (error) => {
  //         setLoading(false);
  //         console.error(error);
  //         openDialogAlert(
  //             'Error al actualizar la suscripción. No se pudo realizar el pago, intente nuevamente',
  //         );
  //     },
  // });

  const [callCreateStudentCredential] = useCreateStudentCredentialMutation({
    client: clientUpload,
    onCompleted: () => {
      callCheckout({
        variables: {
          data: {
            email: user?.email || '',
            priceId: plan?.stripe?.price_id || '',
            stateUrl: `post_url=${getPostUrlWithoutQuery()}`,
            metadata: {
              subscriptionBilling: JSON.stringify(subscriptionBilling),
              post_url: getPostUrlWithoutQuery(),
            },
            coupon: coupon?.coupon ?? undefined,
            plan: {
              title_front: plan?.title_front,
            },
            currentUrl: window.location.href,
          },
        },
      });
    },
    onError: (error) => {
      setLoading(false);
      console.error(error);
      openDialogAlert('Error al guardar la credencial estudiantil');
    },
  });

  const handleCheckout = (): void => {
    if (!plan?.stripe || !user) {
      return;
    }

    if (!interests?.length) {
      openDialogAlert('Seleccione al menos una pagina de interés');
      return;
    }

    const subscriptionBillingChecked =
      subscriptionBilling &&
      Object.keys(subscriptionBilling).find(
        (key) =>
          !subscriptionBilling[key as keyof SubscriptionBillingInput] &&
          key !== 'phone1' &&
          key !== 'address1' &&
          key !== 'internal_number' &&
          key !== 'country' &&
          key !== 'aditional_name',
      );

    if (
      plan?.subscription_billing_is_required &&
      subscriptionBillingChecked &&
      (!subscriptionBilling?.address ||
        !subscriptionBilling?.city ||
        !subscriptionBilling?.external_number ||
        !subscriptionBilling?.phone ||
        !subscriptionBilling?.postal_code ||
        !subscriptionBilling?.state)
    ) {
      openDialogAlert('Los datos de envio son obligatorios para el plan anual');
      return;
    }

    if (!subscriptionBilling?.state || !subscriptionBilling?.phone) {
      openDialogAlert(
        'Los datos sobre el estado y telefono son necesarios para actualizar al plan mensual',
      );
      return;
    }
    if (plan.subscription_student_credential_is_required) {
      const studentCredentialChecked: boolean =
        studentCredential?.file === null;

      if (studentCredentialChecked) {
        openDialogAlert(
          'Los datos de la credencial estudiantil son obligatorios',
        );
        return;
      }

      //setLoading(true);

      callCreateStudentCredential({
        variables: {
          data: {
            file: studentCredential.file,
          },
        },
      });
      return;
    }

    if (!studentTerms) {
      setLoading(false);
      openDialogAlert('Lea y acepte los términos y condiciones');
      return;
    }

    if (activeSubscription) {
      callUpdate({
        variables: {
          data: {
            subscriptionId: activeSubscription?.transaction_id as string,
            priceId: plan.stripe?.price_id || '',
          },
        },
      });
      return;
    }
    callCheckout({
      variables: {
        data: {
          email: user?.email || '',
          priceId: plan?.stripe?.price_id || '',
          stateUrl: `post_url=${getPostUrlWithoutQuery()}`,
          metadata: {
            subscriptionBilling: JSON.stringify(subscriptionBilling),
            post_url: getPostUrlWithoutQuery(),
          },
          coupon: coupon?.coupon ?? undefined,
          plan: {
            title_front: plan.title_front,
          },
          currentUrl: window.location.href,
        },
      },
    });
  };
  useEffect(() => {
    const subscriptionBillingChecked =
      subscriptionBilling &&
      Object.keys(subscriptionBilling).find(
        (key) =>
          !subscriptionBilling[key as keyof SubscriptionBillingInput] &&
          key !== 'phone1' &&
          key !== 'address1' &&
          key !== 'internal_number' &&
          key !== 'country' &&
          key !== 'aditional_name',
      );
    //console.log('revision',!subscriptionBilling?.postal_code, subscriptionBilling?.postal_code);
    if (
      plan?.subscription_billing_is_required &&
      subscriptionBillingChecked &&
      (!subscriptionBilling?.address ||
        !subscriptionBilling?.city ||
        !subscriptionBilling?.external_number ||
        !subscriptionBilling?.phone ||
        !subscriptionBilling?.postal_code ||
        !subscriptionBilling?.state)
    ) {
      setBillingDisabled(true);
      return;
    }
    if (!subscriptionBilling?.state || !subscriptionBilling?.phone) {
      setBillingDisabled(true);
      return;
    }

    if (!interests?.length) {
      setBillingDisabled(true);
      return;
    }

    if (plan?.subscription_student_credential_is_required) {
      const studentCredentialChecked: boolean =
        studentCredential?.file === null;

      if (studentCredentialChecked) {
        setBillingDisabled(true);
        return;
      }
      if (!studentTerms) {
        setBillingDisabled(true);
        return;
      }
    }
    setBillingDisabled(false);
  }, [
    subscriptionBilling,
    subscriptionBilling?.postal_code,
    studentCredential,
    plan,
    user,
    interests?.length,
    studentTerms,
  ]);

  if (!plan?.stripe) {
    return (
      <Typography variant="subtitle1" fontWeight={800} textAlign="center">
        Stripe no esta configurado para este plan, contacte a su administrador
      </Typography>
    );
  }

  return (
    <>
      {loading ? (
        <Box
          position={'fixed'}
          zIndex={99999999}
          top={0}
          left={0}
          width={'100%'}
          height={'100%'}
          component={'div'}
          sx={{ '-webkit-backdrop-filter': 'blur(10px) brightness(0.8)' }}
          style={{
            backdropFilter: 'blur(10px) brightness(0.8)',
            background: 'rgba(0, 0, 0, 0.3)',
          }}
        >
          <Box
            display={'flex'}
            justifyContent={'center'}
            alignItems={'center'}
            height={'100%'}
          >
            <Typography fontFamily={'manop'} color={'white'} fontSize={'32px'}>
              Procesando, por favor espere…
            </Typography>
          </Box>
        </Box>
      ) : null}
      <ButtonDefault
        variant="contained"
        size="large"
        fullWidth
        disabled={loading || billingDisabled}
        isLoading={loading || loadingUpdate}
        onClick={() => {
          setLoading(true);
          if (plan.title_front?.toLowerCase().includes('estándar')) {
            triggerEvent('btn_mensual_tdc');
          } else if (plan.title_front?.toLowerCase()?.includes('profesional')) {
            triggerEvent('btn_anual_tdc');
          } else {
            triggerEvent('btn-est-tdc');
          }
          if (
            plan?.subscription_student_credential_is_required &&
            billingDisabled
          ) {
            openDialogAlert(
              'Los datos de la credencial estudiantil son obligatorios',
            );
          }
          updateUserPreferences({
            variables: {
              data: {
                user_id: user?.id ?? 0,
                must_send: broadcastData?.must_send,
                phone: broadcastData?.phone,
                interests: translateInterests(interests ?? []),
              },
            },
            onCompleted: () => {
              handleCheckout();
            },
          });
        }}
        className={classes.button}
      >
        TARJETA DE DÉBITO/CRÉDITO
      </ButtonDefault>
    </>
  );
};
